// ============================================================================
// TASK RUN PAGE (owner / team)
// Shared task runner at /tasks/run/:propertyId?taskId=...
// Reuses CleaningTab, InventoryCheckTab, CleaningTimer; no "View as cleaner".
// ============================================================================

import { useState, useEffect, useRef, useMemo, useCallback, type ReactNode } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, PartyPopper, ChevronLeft, Clock, Loader2, CalendarDays } from "lucide-react";
import Navbar from "@/components/Navbar";
import { AccessInstructionsView } from "@/components/owner/AccessInstructionsView";
import CleaningTab from "@/components/cleaner/CleaningTab";
import InventoryCheckTab from "@/components/cleaner/InventoryCheckTab";
import { SimpleInventoryStep } from "@/components/cleaner/SimpleInventoryStep";
import { GuestAmenitySetupStep, buildLeftOutRecallFromStaging } from "@/components/cleaner/GuestAmenitySetupStep";
import { parseGuestSetupState } from "@/types/lifecycle";
import CleaningTimer from "@/components/cleaner/CleaningTimer";
import { EntryWalkthroughStep } from "@/components/cleaner/EntryWalkthroughStep";
import CheckoutTimeBanner from "@/components/cleaner/CheckoutTimeBanner";
import { TaskPreStartView } from "@/components/cleaner/TaskPreStartView";
import {
  TurnoverStepStrip,
  resolveTurnoverPhase,
  getTurnoverFlowSummary,
} from "@/components/cleaner/TurnoverStepStrip";
import { TurnoverWizard } from "@/components/cleaner/TurnoverWizard";
import {
  buildTurnoverWizardSteps,
  getActiveWizardStepId,
  simpleFlowSegmentIndex,
  type TurnoverWizardContext,
} from "@/lib/turnoverWizardPlan";
import {
  buildSimpleFlowRewind,
  collectSimplePhotoUrls,
  isSimpleDropKeysComplete,
  isSimpleTurnoverFullyDone,
  previousSimpleSegmentIndex,
  readSimpleFlowState,
  simpleFlowSegments,
  simpleSegmentIdFromIndex,
  SIMPLE_TURNOVER_V1,
} from "@/lib/simpleTurnover";
import { toast } from "@/hooks/use-toast";
import { RoomAwareStep } from "@/components/cleaner/RoomAwareStep";
import { SimpleDropKeysStep } from "@/components/cleaner/SimpleDropKeysStep";
import { SimpleQuickCheckStep } from "@/components/cleaner/SimpleQuickCheckStep";
import { SimpleCleanStep } from "@/components/cleaner/SimpleCleanStep";
import { SimpleKeysStep } from "@/components/cleaner/SimpleKeysStep";
import { SimpleMaintenanceStep } from "@/components/cleaner/SimpleMaintenanceStep";
import { SimpleDoneStep } from "@/components/cleaner/SimpleDoneStep";
import { TurnoverUnitIssueReportDialog } from "@/components/cleaner/TurnoverUnitIssueReportDialog";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useElapsedSinceLabel } from "@/hooks/useElapsedSince";
import { AppLayout } from "@/components/layout/AppLayout";
import { TestCleanBar, TestCleanEndSheet, TestCleanFinished } from "@/components/owner/TestCleanChrome";
import { deleteTestClean, endTestCleanKeep } from "@/lib/testClean";
import { useTaskForOwnerOrTeamRunner } from "@/hooks/useTaskForOwnerOrTeamRunner";
import { usePropertyWithAccess } from "@/hooks/useProperties";
import { setActiveJobClock } from "@/lib/activeJobClock";
import { useStartCleaningTimer } from "@/hooks/useCurrentCleaningTask";
import { useMarkTaskCompleteAsOwnerOrTeam, useSubmitCleaningAsOwnerOrTeam, useSubmitInventoryAsOwnerOrTeam } from "@/hooks/useCleaner";
import { useInventoryForCleaner } from "@/hooks/useCleaner";
import { computeEffectiveTimes } from "@/lib/effectiveTimes";
import { formatScheduledDate } from "@/lib/dateUtils";
import { useRefreshTurnoverWindow } from "@/hooks/useRefreshTurnoverWindow";
import { TURNOVER_RUNNER_NAVBAR_CLASSNAME } from "@/lib/turnoverChrome";
import { useTranslation } from "@/hooks/useTranslation";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useWalkQuickChecks } from "@/hooks/useWalkQuickChecks";
import { useDueCleanerMaintenanceChecks } from "@/hooks/useDueMaintenanceChecks";
import { SimpleMaintenanceDueStep } from "@/components/cleaner/SimpleMaintenanceDueStep";

type CleaningFlowState = "checklist" | "inventory" | "complete";

function invalidateTaskRunnerQuery(queryClient: QueryClient, propertyId: string | undefined, taskId: string | null) {
  void queryClient.invalidateQueries({ queryKey: ["task-for-owner-team-runner", propertyId, taskId] });
}

const TaskRunPage = () => {
  const { propertyId } = useParams();
  const [searchParams] = useSearchParams();
  const taskIdFromUrl = searchParams.get("taskId");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tDynamic } = useTranslation();
  const { user } = useAuth();

  const [preStartStartClicked, setPreStartStartClicked] = useState(false);
  const [preStartStarting, setPreStartStarting] = useState(false);
  const [activeTab, setActiveTab] = useState<"cleaning" | "inventory">("cleaning");
  const [cleaningFlowState, setCleaningFlowState] = useState<CleaningFlowState>("checklist");
  const [progressData, setProgressData] = useState({ completedCount: 0, totalTasks: 0, isSaving: false });
  const [unitIssueReportOpen, setUnitIssueReportOpen] = useState(false);
  const stateInitializedRef = useRef<string | false>(false);

  const { task, allowed, errorMessage, isLoading: taskLoading } = useTaskForOwnerOrTeamRunner(propertyId, taskIdFromUrl ?? undefined);
  const effectivePropertyId = task?.property_id ?? propertyId;
  useRefreshTurnoverWindow({
    propertyId: effectivePropertyId,
    taskId: task?.id ?? taskIdFromUrl,
    enabled: Boolean(effectivePropertyId && (task?.id || taskIdFromUrl)),
  });
  const { data: property } = usePropertyWithAccess(effectivePropertyId);
  const { data: walkQuickChecks = [] } = useWalkQuickChecks(effectivePropertyId);
  const { due: dueMaintChecks } = useDueCleanerMaintenanceChecks(
    effectivePropertyId,
    task?.scheduled_date,
  );
  const includeMaintenance = dueMaintChecks.length > 0;
  const includeQuickCheck = walkQuickChecks.length > 0;
  const guestStagingLineCount = task?.checklist_guest_amenity_staging?.length ?? 0;
  const includeGuestSetup = guestStagingLineCount > 0;
  const flowChrome = useMemo(
    () => ({ includeMaintenance, includeQuickCheck, includeGuestSetup }),
    [includeMaintenance, includeQuickCheck, includeGuestSetup],
  );
  const { data: inventoryItems } = useInventoryForCleaner(effectivePropertyId);
  const startCleaningMutation = useStartCleaningTimer();

  // Slice 49: running job clock shown above the step strip.
  useEffect(() => {
    const j = task;
    if (j && j.status === "in_progress" && j.started_at) {
      setActiveJobClock({
        startedAt: j.started_at,
        locationNotSaved: j.start_location_lat == null && !!j.start_location_override_reason,
      });
    } else {
      setActiveJobClock(null);
    }
    return () => setActiveJobClock(null);
  }, [task?.id, task?.status, task?.started_at, task?.start_location_lat, task?.start_location_override_reason]);
  const markTaskCompleteMutation = useMarkTaskCompleteAsOwnerOrTeam();
  const submitCleaningAsOwnerOrTeam = useSubmitCleaningAsOwnerOrTeam(task?.id, effectivePropertyId);
  const submitInventoryAsOwnerOrTeam = useSubmitInventoryAsOwnerOrTeam(task?.id, effectivePropertyId);

  useEffect(() => {
    if (!taskIdFromUrl || !task?.property_id || !propertyId) return;
    if (task.property_id === propertyId) return;
    const params = new URLSearchParams(searchParams);
    navigate(`/tasks/run/${task.property_id}?${params.toString()}`, { replace: true });
  }, [taskIdFromUrl, task?.property_id, propertyId, navigate, searchParams]);

  const checklistDone = !!task?.checklist_completed;
  const inventoryDone = !!task?.inventory_completed;
  const inventoryIncluded = task?.include_inventory_check !== false;

  const turnoverInventoryMeta = useMemo(() => {
    const items = (inventoryItems ?? []) as { inventory_scope?: string | null }[];
    const post = items.filter((i) => i.inventory_scope !== "unit");
    const guest = post.filter((i) => !i.inventory_scope || i.inventory_scope === "guest_amenities").length;
    const supply = post.filter((i) => i.inventory_scope === "supply_closet").length;
    return { postCleanCount: post.length, guestAmenitiesCount: guest, supplyClosetCount: supply };
  }, [inventoryItems]);

  const postCleanHasInventory = turnoverInventoryMeta.postCleanCount > 0 && inventoryIncluded;
  const dropKeysDone = isSimpleDropKeysComplete(readSimpleFlowState(task?.entry_walkthrough_state));
  const taskStatusCompleted = task?.status === "completed";
  const simpleFullyDone = isSimpleTurnoverFullyDone({
    entryWalkthroughState: task?.entry_walkthrough_state,
    checklistCompleted: task?.checklist_completed,
    inventoryCompleted: task?.inventory_completed,
    includeInventoryCheck: task?.include_inventory_check,
    hasInventoryItems: postCleanHasInventory,
    taskStatus: task?.status,
  });

  const showEntryWalkthrough = Boolean(
    task &&
      task.status === "in_progress" &&
      !task.entry_walkthrough_completed &&
      !!task.checklist_id &&
      !!propertyId
  );

  const bothCompleted = SIMPLE_TURNOVER_V1
    ? cleaningFlowState === "complete" && simpleFullyDone
    : cleaningFlowState === "complete";
  const showBothTabs =
    !!task &&
    (cleaningFlowState === "checklist" || cleaningFlowState === "inventory") &&
    turnoverInventoryMeta.guestAmenitiesCount > 0 &&
    turnoverInventoryMeta.supplyClosetCount > 0 &&
    task.include_inventory_check !== false;

  const turnoverPhase = useMemo(
    () =>
      resolveTurnoverPhase({
        showEntryWalkthrough,
        cleaningFlowState,
        inventoryGuestPhaseCompleted: task?.inventory_guest_phase_completed,
        hasSplitPostCleanInventory: showBothTabs,
      }),
    [
      showEntryWalkthrough,
      cleaningFlowState,
      task?.inventory_guest_phase_completed,
      showBothTabs,
    ]
  );

  const showEntryColumn = Boolean(task?.status === "in_progress" && !bothCompleted);

  const flowSummaryShort = useMemo(
    () => getTurnoverFlowSummary(turnoverPhase, showEntryColumn, false, tDynamic),
    [turnoverPhase, showEntryColumn, showBothTabs, tDynamic]
  );

  const turnoverWizardContext = useMemo((): TurnoverWizardContext | null => {
    if (!task || task.status !== "in_progress") return null;
    return {
      status: task.status,
      startedAt: task.started_at,
      entryWalkthroughCompleted: task.entry_walkthrough_completed,
      checklistCompleted: task.checklist_completed,
      inventoryCompleted: task.inventory_completed,
      inventoryGuestPhaseCompleted: task.inventory_guest_phase_completed,
      inventorySupplyPhaseCompleted: task.inventory_supply_phase_completed,
      includeInventoryCheck: task.include_inventory_check,
      postCleanInventoryCount: turnoverInventoryMeta.postCleanCount,
      guestAmenitiesCount: turnoverInventoryMeta.guestAmenitiesCount,
      supplyClosetCount: turnoverInventoryMeta.supplyClosetCount,
      cleaningFlowState,
      entryWalkthroughState: task.entry_walkthrough_state,
      closeoutKeysConfirmed: (task as { closeout_keys_confirmed?: boolean | null }).closeout_keys_confirmed,
      dueMaintenanceCheckIds: dueMaintChecks.map((c) => c.id),
      walkQuickCheckIds: walkQuickChecks.map((c) => c.id),
      guestStagingLineCount: task.checklist_guest_amenity_staging?.length ?? 0,
      guestSetupCompleted: Boolean(task.guest_setup_completed),
    };
  }, [task, turnoverInventoryMeta, cleaningFlowState, dueMaintChecks, walkQuickChecks]);

  const turnoverWizardSteps = useMemo(
    () => (turnoverWizardContext ? buildTurnoverWizardSteps(turnoverWizardContext) : []),
    [turnoverWizardContext]
  );

  const activeWizardStepId = useMemo(
    () => getActiveWizardStepId(turnoverWizardSteps),
    [turnoverWizardSteps]
  );

  // Slice 55 — Test clean: the owner runs the real screens; the bar and End test sit on top.
  const [endTestOpen, setEndTestOpen] = useState(false);
  const [endTestBusy, setEndTestBusy] = useState(false);
  const testStepText = useMemo(() => {
    const flow = turnoverWizardSteps.filter((st) => st.id !== "done");
    const i = Math.max(0, flow.findIndex((st) => st.id === activeWizardStepId));
    return `step ${i + 1} of ${Math.max(1, flow.length)}`;
  }, [turnoverWizardSteps, activeWizardStepId]);

  const inventorySkippedInSimpleFlow = useMemo(() => {
    if (!task) return true;
    return task.include_inventory_check === false || turnoverInventoryMeta.postCleanCount === 0;
  }, [task, turnoverInventoryMeta.postCleanCount]);

  const rewindSimpleToSegment = useCallback(
    async (segmentIndex: number) => {
      if (!task?.id || !SIMPLE_TURNOVER_V1) return;
      let targetIndex = segmentIndex;
      const segs = simpleFlowSegments(flowChrome);
      const invIndex = segs.indexOf("inventory_single");
      const cleanIndex = segs.indexOf("clean_free");
      const guestIndex = segs.indexOf("guest_setup");
      if (inventorySkippedInSimpleFlow && targetIndex === invIndex) {
        targetIndex = guestIndex >= 0 ? guestIndex : cleanIndex;
      }
      const target = simpleSegmentIdFromIndex(targetIndex, flowChrome);
      if (!target) return;
      const activeIndex = simpleFlowSegmentIndex(activeWizardStepId, flowChrome);
      if (targetIndex > activeIndex) return;

      const { flowPatch, clearChecklistCompleted, clearInventoryCompleted, clearGuestSetupCompleted } =
        buildSimpleFlowRewind(target);
      const prev = readSimpleFlowState(task.entry_walkthrough_state);
      const update: Record<string, unknown> = {
        entry_walkthrough_state: { ...prev, ...flowPatch },
      };
      if (clearChecklistCompleted) update.checklist_completed = false;
      if (clearInventoryCompleted) update.inventory_completed = false;
      if (clearGuestSetupCompleted) update.guest_setup_completed = false;
      if (target === "keys" || target === "walk") {
        update.entry_walkthrough_completed = false;
      }

      const { error } = await supabase.from("cleaning_tasks").update(update).eq("id", task.id);
      if (error) {
        toast({ title: "Couldn’t go back", description: error.message, variant: "destructive" });
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ["task-for-owner-team-runner", propertyId] });
    },
    [activeWizardStepId, flowChrome, inventorySkippedInSimpleFlow, propertyId, queryClient, task],
  );

  // Back leaves the job; everything done so far is already saved, so reopening resumes the same step.
  const goBackSimpleStep = useCallback(() => {
    navigate(propertyId ? `/properties/${propertyId}?tab=schedule` : "/owner/properties");
  }, [navigate, propertyId]);

  const guestStagingLineCountForSetup = task?.checklist_guest_amenity_staging?.length ?? 0;

  const needsGuestAmenitySetup = Boolean(
    task &&
      guestStagingLineCountForSetup > 0 &&
      !task.guest_setup_completed &&
      cleaningFlowState !== "complete"
  );

  const leftOutRecall = useMemo(() => {
    if (!task?.checklist_guest_amenity_staging?.length) return null;
    const setup = parseGuestSetupState(task.guest_setup_state);
    return buildLeftOutRecallFromStaging(
      task.checklist_guest_amenity_staging,
      setup.left_out_by_staging_id,
      task.guest_count,
    );
  }, [task?.checklist_guest_amenity_staging, task?.guest_setup_state, task?.guest_count]);

  const showGuestAmenitySetupLegacy = Boolean(
    turnoverWizardSteps.length === 0 && needsGuestAmenitySetup && activeTab === "inventory"
  );

  const showTurnoverWizard = Boolean(turnoverWizardSteps.length > 0);

  const timerStartedAtForProperty =
    task?.status === "in_progress" && task?.started_at ? task.started_at : undefined;

  const elapsedLabel = useElapsedSinceLabel(timerStartedAtForProperty);
  const minimalTurnoverChrome = Boolean(
    SIMPLE_TURNOVER_V1
      ? showTurnoverWizard && activeWizardStepId !== "start_visit" && activeWizardStepId !== "done"
      : showTurnoverWizard && timerStartedAtForProperty
  );

  const hideSiteNavbar = Boolean(
    SIMPLE_TURNOVER_V1 &&
      ((task?.status === "scheduled" && !timerStartedAtForProperty) ||
        (showTurnoverWizard && activeWizardStepId !== "start_visit"))
  );

  const inventoryPageSubtitle = useMemo(() => {
    const name = (task?.property_name ?? property?.name ?? "").trim() || null;
    const unit = (property?.unit as string | undefined)?.trim();
    const unitLoc = (property?.unit_location as string | undefined)?.trim();
    const detail = [unit, unitLoc].filter(Boolean).join(" · ") || null;
    if (name && detail) return `${name} · ${detail}`;
    if (name) return name;
    return detail;
  }, [task?.property_name, property?.name, property?.unit, property?.unit_location]);

  useEffect(() => {
    if (!task?.id) return;
    const signature = `${task.id}-${checklistDone}-${inventoryDone}-${dropKeysDone}-${taskStatusCompleted}-${SIMPLE_TURNOVER_V1}`;
    if (stateInitializedRef.current === signature) return;
    if (SIMPLE_TURNOVER_V1) {
      if (simpleFullyDone || taskStatusCompleted) {
        setCleaningFlowState("complete");
      } else if (checklistDone && postCleanHasInventory && !inventoryDone) {
        setCleaningFlowState("inventory");
      } else {
        setCleaningFlowState("checklist");
      }
    } else if (checklistDone && inventoryDone) {
      setCleaningFlowState("complete");
    } else if (checklistDone) {
      setCleaningFlowState(postCleanHasInventory ? "inventory" : "complete");
    } else {
      setCleaningFlowState("checklist");
    }
    stateInitializedRef.current = signature;
  }, [
    task?.id,
    checklistDone,
    inventoryDone,
    postCleanHasInventory,
    dropKeysDone,
    taskStatusCompleted,
    simpleFullyDone,
  ]);

  const handleChecklistSubmitted = async () => {
    if (SIMPLE_TURNOVER_V1) {
      // After photos drive checklist_completed in simple flow — do not finish the job here.
      void queryClient.invalidateQueries({ queryKey: ["task-for-owner-team-runner", propertyId] });
      return;
    }
    const nextState = postCleanHasInventory ? "inventory" : "complete";
    setCleaningFlowState(nextState);
    if (nextState === "complete" && task?.id && !postCleanHasInventory) {
      try {
        await markTaskCompleteMutation.mutateAsync({ taskId: task.id, propertyId: propertyId! });
      } catch (e) {
        console.error("[TaskRunPage] markTaskComplete failed:", e);
          // Still show complete state; task may already be completed from submit
      }
    }
    queryClient.invalidateQueries({ queryKey: ["task-for-owner-team-runner", propertyId] });
    queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", propertyId] });
    queryClient.invalidateQueries({ queryKey: ["my-tasks"] });
  };

  const handleInventorySubmitted = () => {
    if (SIMPLE_TURNOVER_V1) {
      // Inventory is mid-flow (before After + Drop). Do not mark job complete.
      void queryClient.invalidateQueries({ queryKey: ["task-for-owner-team-runner", propertyId] });
      void queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", propertyId] });
      return;
    }
    setCleaningFlowState("complete");
    queryClient.invalidateQueries({ queryKey: ["task-for-owner-team-runner", propertyId] });
    queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", propertyId] });
    queryClient.invalidateQueries({ queryKey: ["my-tasks"] });
  };

  useEffect(() => {
    if (cleaningFlowState === "inventory") {
      setActiveTab((prev) => (prev === "inventory" ? prev : "inventory"));
    } else {
      setActiveTab((prev) => (prev === "cleaning" ? prev : "cleaning"));
    }
  }, [cleaningFlowState]);

  const propertyName = task?.property_name || property?.name || null;
  const propertyAddress = task?.property_address || property?.address || null;
  const propertyPhotoUrl = property?.property_photo_url ?? null;

  const wrapRunner = (children: ReactNode, opts?: { hideNavbar?: boolean }) => (
    <AppLayout>
      <div data-cleaner-turnover-chrome="1" className="min-w-0 bg-[color:var(--pv-bg)]">
        {!(opts?.hideNavbar ?? hideSiteNavbar) ? (
          <Navbar className={TURNOVER_RUNNER_NAVBAR_CLASSNAME} />
        ) : null}
        {task?.is_test && activeWizardStepId !== "done" ? <TestCleanBar onEnd={() => setEndTestOpen(true)} /> : null}
        {children}
        <TestCleanEndSheet
          open={endTestOpen}
          stepText={testStepText}
          busy={endTestBusy}
          onClose={() => setEndTestOpen(false)}
          onSave={async () => {
            if (!task) return;
            setEndTestBusy(true);
            try {
              await endTestCleanKeep(task.id, task.test_setup);
              setEndTestOpen(false);
              void queryClient.invalidateQueries({ queryKey: ["task-for-owner-team-runner"] });
              void queryClient.invalidateQueries({ queryKey: ["cleaning-tasks"] });
              navigate(`/properties/${task.property_id}?tab=reports`);
            } catch (error) {
              toast({
                title: tDynamic("Error"),
                description: error instanceof Error ? error.message : tDynamic("Could not save"),
                variant: "destructive",
              });
            } finally {
              setEndTestBusy(false);
            }
          }}
          onDiscard={async () => {
            if (!task) return;
            setEndTestBusy(true);
            try {
              await deleteTestClean(task.id);
              setEndTestOpen(false);
              void queryClient.invalidateQueries({ queryKey: ["cleaning-tasks"] });
              void queryClient.invalidateQueries({ queryKey: ["my-tasks"] });
              toast({ title: "Test clean thrown away. Nothing saved." });
              navigate(`/properties/${task.property_id}`);
            } catch (error) {
              toast({
                title: tDynamic("Error"),
                description: error instanceof Error ? error.message : tDynamic("Could not delete"),
                variant: "destructive",
              });
            } finally {
              setEndTestBusy(false);
            }
          }}
        />
      </div>
    </AppLayout>
  );

  if (taskLoading || (!task && !errorMessage)) {
    return wrapRunner(
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 px-4">
        <Loader2 className="h-8 w-8 animate-spin text-[color:var(--pv-teal)]" aria-hidden />
        <p className="text-sm text-[color:var(--pv-ink-2)]">{tDynamic("Loading...")}</p>
      </div>
    );
  }

  if (!taskIdFromUrl || !propertyId) {
    return wrapRunner(
      <div className="flex min-h-[50vh] items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{tDynamic("Task not found")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4 text-muted-foreground">
              {tDynamic("Missing task or property. Use My tasks to start a task.")}
            </p>
            <Button onClick={() => navigate("/owner/properties")}>{tDynamic("Go to dashboard")}</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!allowed) {
    return wrapRunner(
      <div className="flex min-h-[50vh] items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{tDynamic("Can't complete this task")}</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>
                {errorMessage ??
                  tDynamic(
                    "Task assigned to you, but you don't have permission to run tasks. Ask the owner to enable 'Can do tasks'."
                  )}
              </AlertDescription>
            </Alert>
            <p className="mb-4 text-sm text-muted-foreground">
              {tDynamic("Ask the owner to enable 'Can do tasks' for you in Settings → Access & Team.")}
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              <Button variant="outline" onClick={() => invalidateTaskRunnerQuery(queryClient, propertyId, taskIdFromUrl)}>
                {tDynamic("Retry")}
              </Button>
              <Button onClick={() => navigate("/owner/properties")}>{tDynamic("Go to dashboard")}</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!task) {
    return wrapRunner(
      <div className="flex min-h-[50vh] items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{tDynamic("Task not found")}</CardTitle>
          </CardHeader>
          <CardContent>
            {errorMessage ? <p className="mb-4 text-sm text-muted-foreground">{errorMessage}</p> : null}
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              <Button variant="outline" onClick={() => invalidateTaskRunnerQuery(queryClient, propertyId, taskIdFromUrl)}>
                {tDynamic("Retry")}
              </Button>
              <Button onClick={() => navigate("/owner/properties")}>{tDynamic("Go to dashboard")}</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const accessInstructions = property
    ? {
        entry_method: property.entry_method ?? null,
        entry_code: property.entry_code ?? null,
        entry_location: property.entry_location ?? null,
        unit_location: property.unit_location ?? null,
        parking_instructions: property.parking_instructions ?? null,
        supply_closet_info: property.supply_closet_info ?? null,
        access_notes: property.access_notes ?? null,
      }
    : null;

  const isPreStartTask = task.status === "scheduled" && !task.started_at;
  const effectiveTimes = computeEffectiveTimes(
    { override_check_in_time: task.override_check_in_time, override_check_out_time: task.override_check_out_time },
    { check_in_time: task.reservation_check_in_time, check_out_time: task.reservation_check_out_time }
  );

  if (isPreStartTask) {
    return wrapRunner(
      <div
        className={
          SIMPLE_TURNOVER_V1
            ? "min-h-[100dvh] w-full min-w-0 bg-white"
            : "mx-auto max-w-2xl px-4 py-4 sm:px-6 sm:py-6"
        }
      >
        <TaskPreStartView
          task={task}
          propertyName={propertyName}
          propertyAddress={propertyAddress}
          propertyPhotoUrl={propertyPhotoUrl}
          accessInstructions={accessInstructions}
          isStarting={preStartStarting || startCleaningMutation.isPending}
          onStart={async () => {
            if (SIMPLE_TURNOVER_V1) {
              setPreStartStarting(true);
              try {
                await startCleaningMutation.mutateAsync({
                  taskId: task.id,
                  propertyId: propertyId!,
                });
                void queryClient.invalidateQueries({
                  queryKey: ["task-for-owner-team-runner", propertyId, taskIdFromUrl],
                });
                void queryClient.invalidateQueries({ queryKey: ["my-tasks"] });
              } catch (error) {
                console.error("[TaskRunPage] Failed to start task:", error);
                toast({
                  title: tDynamic("Error"),
                  description:
                    error instanceof Error ? error.message : tDynamic("Could not start task"),
                  variant: "destructive",
                });
              } finally {
                setPreStartStarting(false);
              }
              return;
            }
            setPreStartStartClicked(true);
          }}
          onBack={() => navigate("/owner/properties")}
          startButtonLabel={SIMPLE_TURNOVER_V1 ? "Start" : tDynamic("Start task")}
          windowLabel={task.window_label ?? null}
          windowTimes={
            (task as { window_times_detail_primary?: string | null }).window_times_detail_primary ??
            task.window_times_label ??
            null
          }
          windowTimesSecondary={
            (task as { window_times_detail_secondary?: string | null }).window_times_detail_secondary ??
            (task as { window_times_secondary?: string | null }).window_times_secondary ??
            null
          }
          windowTimesTone={
            ((task as { window_times_tone?: string | null }).window_times_tone as
              | "default"
              | "teal"
              | "amber"
              | "muted"
              | null) ?? null
          }
          windowChip={task.window_chip ?? null}
          guestLeft={Boolean(task.guest_left)}
          waitingOnGuest={Boolean((task as { waiting_on_guest?: boolean }).waiting_on_guest)}
          sameDay={Boolean(task.same_day_turnover)}
          tightWindow={Boolean(task.tight_window)}
        />
      </div>,
      { hideNavbar: SIMPLE_TURNOVER_V1 }
    );
  }

  if (!SIMPLE_TURNOVER_V1 && task.status === "scheduled" && preStartStartClicked) {
    const handleStart = () => {
      startCleaningMutation.mutate(
        { taskId: task.id, propertyId },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["task-for-owner-team-runner", propertyId] });
          },
        }
      );
    };
    return wrapRunner(
      <div className="mx-auto max-w-2xl px-4 py-4 sm:px-6 sm:py-6">
        <Button variant="ghost" size="sm" className="mb-4 -ml-2" onClick={() => setPreStartStartClicked(false)}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            {tDynamic("Back")}
          </Button>
          <CleaningTimer
            taskId={task.id}
            propertyId={propertyId}
            startedAt={undefined}
            completedAt={undefined}
            durationMinutes={undefined}
            scheduledDate={task.scheduled_date}
            unitEmptyOnArrival={undefined}
            propertyLat={property?.latitude ?? undefined}
            propertyLng={property?.longitude ?? undefined}
            propertyAddress={property?.formatted_address || [property?.address, property?.city].filter(Boolean).join(", ") || undefined}
            confirmLocationStepEnabled
          />
          <div className="mt-4">
            <Button onClick={handleStart} disabled={startCleaningMutation.isPending} className="w-full">
              {startCleaningMutation.isPending ? tDynamic("Starting...") : tDynamic("Start task")}
            </Button>
          </div>
      </div>
    );
  }

  if (bothCompleted) {
    return wrapRunner(
      <div className="mx-auto max-w-2xl px-4 py-4 sm:px-6 sm:py-6">
        <div className="mb-4">
          {propertyPhotoUrl ? (
            <div className="mb-3 overflow-hidden rounded-xl">
              <img src={propertyPhotoUrl} alt={propertyName ?? ""} className="h-28 w-full object-cover" />
            </div>
          ) : (
            <div className="mb-3 flex h-20 items-center justify-center rounded-xl bg-muted">
              <p className="text-xs text-muted-foreground">{tDynamic("No property photo")}</p>
            </div>
          )}
          <h1 className="text-xl font-bold text-[color:var(--pv-ink)]">{propertyName ?? tDynamic("Task")}</h1>
          <p className="text-sm text-[color:var(--pv-ink-2)]">{propertyAddress ?? ""}</p>
        </div>
        <Card className="rounded-[var(--pv-r-md)] border-[color:color-mix(in_srgb,var(--pv-teal)_28%,var(--pv-line))] bg-[color:var(--pv-teal-soft)] shadow-[var(--pv-shadow-card)]">
          <CardContent className="py-6 text-center">
            <PartyPopper className="mx-auto mb-3 h-12 w-12 text-[color:var(--pv-teal)]" />
            <h2 className="mb-2 text-xl font-bold text-[color:var(--pv-ink)]">{tDynamic("Task completed!")}</h2>
            {task.duration_minutes != null && (
              <p className="mt-2 text-sm font-medium text-[color:var(--pv-teal)]">
                {tDynamic("Time:")} {task.duration_minutes} {tDynamic("minutes")}
              </p>
            )}
          </CardContent>
        </Card>
        <Card className="mt-4 rounded-[var(--pv-r-md)] border-[color:var(--pv-line)] bg-[color:var(--pv-surface)] shadow-[var(--pv-shadow-card)]">
          <CardContent className="flex flex-col items-center gap-4 py-5 text-center">
            <CalendarDays className="h-9 w-9 text-[color:var(--pv-ink-3)]" aria-hidden />
            <p className="text-sm text-[color:var(--pv-ink-2)]">{tDynamic("You can return to the dashboard when you're ready.")}</p>
            <Button className="w-full sm:w-auto" onClick={() => navigate("/owner/properties")}>
              {tDynamic("Back to dashboard")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const PropertyHeader = () => (
    <div className="mb-4 sm:mb-6">
      {propertyPhotoUrl ? (
        <div className="mb-3 rounded-xl overflow-hidden">
          <img src={propertyPhotoUrl} alt={propertyName ?? ""} className="w-full h-28 object-cover" />
        </div>
      ) : (
        <div className="mb-3 rounded-xl bg-muted h-20 flex items-center justify-center">
          <p className="text-muted-foreground text-xs">{tDynamic("No property photo")}</p>
        </div>
      )}
      <h1 className="text-xl sm:text-2xl font-bold">{propertyName ?? tDynamic("Task")}</h1>
      <p className="text-sm text-muted-foreground">{propertyAddress ?? ""}</p>
      {accessInstructions && (
        <div className="mt-3">
          <AccessInstructionsView instructions={accessInstructions} defaultOpen />
        </div>
      )}
    </div>
  );

  return wrapRunner(
    <>
      <div
        className={
          SIMPLE_TURNOVER_V1 && showTurnoverWizard
            ? "w-full min-w-0 bg-white"
            : "mx-auto max-w-2xl space-y-4 px-4 py-4 sm:px-6 sm:py-6"
        }
      >
        {!(SIMPLE_TURNOVER_V1 && showTurnoverWizard && activeWizardStepId !== "start_visit" && activeWizardStepId !== "done") ? (
          <Button variant="ghost" size="sm" className="-ml-2" onClick={() => navigate("/owner/properties")}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            {tDynamic("Back to dashboard")}
          </Button>
        ) : null}
        {!minimalTurnoverChrome && !SIMPLE_TURNOVER_V1 ? (
          <>
            <PropertyHeader />
            <p className="text-xs text-muted-foreground -mt-2 mb-1" role="status">
              {flowSummaryShort}
            </p>
            <p className="text-xs sm:text-sm font-medium text-primary mb-4">
              {tDynamic("Scheduled:")} {formatScheduledDate(task.scheduled_date, "MMMM d, yyyy")}
            </p>
            {task.window_times_label ? (
              <div className="mb-4 rounded-lg border bg-muted/30 px-3 py-2 text-sm">
                <span className="font-medium">{task.window_times_label}</span>
              </div>
            ) : task.reservation_check_in_time || task.reservation_check_out_time ? (
              <div className="mb-4 rounded-lg border bg-muted/30 px-3 py-2 text-sm">
                <span className="text-muted-foreground">{tDynamic("Reservation:")} </span>
                {task.reservation_check_in_time && <span>{tDynamic("Check-in")} {task.reservation_check_in_time}</span>}
                {task.reservation_check_in_time && task.reservation_check_out_time && " · "}
                {task.reservation_check_out_time && <span>{tDynamic("Check-out")} {task.reservation_check_out_time}</span>}
              </div>
            ) : null}
            <CheckoutTimeBanner
              checkoutTime={effectiveTimes.checkOut}
              earlyCheckoutDetected={Boolean(task.guest_left)}
              unitEmptyOnArrival={task.unit_empty_on_arrival}
              sticky
            />
          </>
        ) : null}
        {showTurnoverWizard ? (
          <TurnoverWizard
            steps={turnoverWizardSteps}
            activeStepId={activeWizardStepId}
            tDynamic={tDynamic}
            variant={SIMPLE_TURNOVER_V1 ? "bare" : "legacy"}
            timerSummary={
              !SIMPLE_TURNOVER_V1 && elapsedLabel ? (
                <span className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  {elapsedLabel}
                </span>
              ) : undefined
            }
          >
            {activeWizardStepId === "start_visit" && !SIMPLE_TURNOVER_V1 && (
              <CleaningTimer
                taskId={task.id}
                propertyId={propertyId!}
                startedAt={timerStartedAtForProperty ?? null}
                completedAt={undefined}
                durationMinutes={undefined}
                scheduledDate={task.scheduled_date}
                unitEmptyOnArrival={undefined}
                propertyLat={property?.latitude ?? undefined}
                propertyLng={property?.longitude ?? undefined}
                propertyAddress={
                  property?.formatted_address || [property?.address, property?.city].filter(Boolean).join(", ") || undefined
                }
                confirmLocationStepEnabled
              />
            )}
            {activeWizardStepId === "start_visit" && SIMPLE_TURNOVER_V1 && (
              <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 px-6 py-10">
                <Loader2 className="h-8 w-8 animate-spin text-[#24a89a]" aria-hidden />
                <p className="text-sm font-medium text-[#64748b]">{tDynamic("Starting...")}</p>
                <Button
                  type="button"
                  className="mt-2 w-full max-w-sm"
                  disabled={startCleaningMutation.isPending}
                  onClick={async () => {
                    try {
                      await startCleaningMutation.mutateAsync({
                        taskId: task.id,
                        propertyId: propertyId!,
                      });
                      invalidateTaskRunnerQuery(queryClient, propertyId, task.id);
                    } catch (error) {
                      toast({
                        title: tDynamic("Error"),
                        description:
                          error instanceof Error ? error.message : tDynamic("Could not start task"),
                        variant: "destructive",
                      });
                    }
                  }}
                >
                  {startCleaningMutation.isPending ? tDynamic("Starting...") : "Start"}
                </Button>
              </div>
            )}
            {activeWizardStepId !== "start_visit" && timerStartedAtForProperty && !SIMPLE_TURNOVER_V1 && (
              <CleaningTimer
                taskId={task.id}
                propertyId={propertyId!}
                startedAt={timerStartedAtForProperty}
                completedAt={task.completed_at ?? undefined}
                durationMinutes={task.duration_minutes ?? undefined}
                scheduledDate={task.scheduled_date}
                unitEmptyOnArrival={task.unit_empty_on_arrival ?? undefined}
                propertyLat={property?.latitude ?? undefined}
                propertyLng={property?.longitude ?? undefined}
                propertyAddress={
                  property?.formatted_address || [property?.address, property?.city].filter(Boolean).join(", ") || undefined
                }
                confirmLocationStepEnabled={false}
                hideRunningElapsedCard
              />
            )}
            {activeWizardStepId === "arrival_check" && (
              <EntryWalkthroughStep
                propertyId={propertyId!}
                taskId={task.id}
                checklistId={task.checklist_id}
                cleanerId=""
                onCompleted={() => {
                  void queryClient.invalidateQueries({ queryKey: ["task-for-owner-team-runner", propertyId] });
                }}
                tDynamic={tDynamic}
              />
            )}
            {SIMPLE_TURNOVER_V1 && activeWizardStepId === "walk" && (
              <RoomAwareStep
                phase="before"
                propertyId={propertyId!}
                taskId={task.id}
                cleanerId={null}
                checklistName={task.checklist_name}
                entryWalkthroughState={task.entry_walkthrough_state}
                includeMaintenance={includeMaintenance}
                includeQuickCheck={includeQuickCheck}
                includeGuestSetup={includeGuestSetup}
                onBack={goBackSimpleStep}
                onSelectSegment={(i) => void rewindSimpleToSegment(i)}
                onComplete={() => {
                  void queryClient.invalidateQueries({ queryKey: ["task-for-owner-team-runner", propertyId] });
                }}
              />
            )}
            {SIMPLE_TURNOVER_V1 && activeWizardStepId === "walk_checks" && (
              <SimpleQuickCheckStep
                propertyId={propertyId!}
                taskId={task.id}
                cleanerId={(task as { cleaner_id?: string | null }).cleaner_id ?? null}
                checklistName={task.checklist_name}
                entryWalkthroughState={task.entry_walkthrough_state}
                quickChecks={walkQuickChecks.map((q) => ({ id: q.id, label: q.label }))}
                includeMaintenance={includeMaintenance}
                includeQuickCheck={includeQuickCheck}
                includeGuestSetup={includeGuestSetup}
                onBack={goBackSimpleStep}
                onSelectSegment={(i) => void rewindSimpleToSegment(i)}
                onComplete={() => {
                  void queryClient.invalidateQueries({ queryKey: ["task-for-owner-team-runner", propertyId] });
                }}
              />
            )}
            {SIMPLE_TURNOVER_V1 && activeWizardStepId === "maintenance" && (
              <SimpleMaintenanceDueStep
                propertyId={propertyId!}
                taskId={task.id}
                entryWalkthroughState={task.entry_walkthrough_state}
                dueChecks={dueMaintChecks}
                includeQuickCheck={includeQuickCheck}
                includeGuestSetup={includeGuestSetup}
                onBack={goBackSimpleStep}
                onSelectSegment={(i) => void rewindSimpleToSegment(i)}
                onComplete={() => {
                  void queryClient.invalidateQueries({ queryKey: ["task-for-owner-team-runner", propertyId] });
                }}
              />
            )}
            {SIMPLE_TURNOVER_V1 && activeWizardStepId === "clean_free" && (
              <SimpleCleanStep
                propertyId={propertyId!}
                taskId={task.id}
                entryWalkthroughState={task.entry_walkthrough_state}
                checklistIds={task.assigned_checklists?.length ? task.assigned_checklists.map((c) => c.checklist_id) : task.checklist_id ? [task.checklist_id] : []}
                startedAt={task.started_at}
                includeMaintenance={includeMaintenance}
                includeQuickCheck={includeQuickCheck}
                includeGuestSetup={includeGuestSetup}
                onReportIssue={
                  (task as { cleaner_id?: string | null }).cleaner_id
                    ? () => setUnitIssueReportOpen(true)
                    : undefined
                }
                onBack={goBackSimpleStep}
                onSelectSegment={(i) => void rewindSimpleToSegment(i)}
                onUndoMaintenanceSkip={() => {
                  const maintIndex = simpleFlowSegmentIndex("maintenance", flowChrome);
                  if (maintIndex >= 0) void rewindSimpleToSegment(maintIndex);
                }}
                onComplete={() => {
                  void queryClient.invalidateQueries({ queryKey: ["task-for-owner-team-runner", propertyId] });
                }}
              />
            )}
            {SIMPLE_TURNOVER_V1 && activeWizardStepId === "after_photos" && (
              <RoomAwareStep
                phase="after"
                propertyId={propertyId!}
                taskId={task.id}
                entryWalkthroughState={task.entry_walkthrough_state}
                includeMaintenance={includeMaintenance}
                includeQuickCheck={includeQuickCheck}
                includeGuestSetup={includeGuestSetup}
                onBack={goBackSimpleStep}
                onSelectSegment={(i) => void rewindSimpleToSegment(i)}
                onComplete={() => {
                  void queryClient.invalidateQueries({ queryKey: ["task-for-owner-team-runner", propertyId] });
                }}
              />
            )}
            {SIMPLE_TURNOVER_V1 && activeWizardStepId === "drop_keys" && (
              <SimpleDropKeysStep
                propertyId={propertyId!}
                taskId={task.id}
                entryWalkthroughState={task.entry_walkthrough_state}
                includeMaintenance={includeMaintenance}
                includeQuickCheck={includeQuickCheck}
                includeGuestSetup={includeGuestSetup}
                onBack={goBackSimpleStep}
                onSelectSegment={(i) => void rewindSimpleToSegment(i)}
                onComplete={async () => {
                  try {
                    await markTaskCompleteMutation.mutateAsync({
                      taskId: task.id,
                      propertyId: propertyId!,
                    });
                  } catch {
                    /* still advance */
                  }
                  setCleaningFlowState("complete");
                  void queryClient.invalidateQueries({ queryKey: ["task-for-owner-team-runner", propertyId] });
                  void queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", propertyId] });
                  void queryClient.invalidateQueries({ queryKey: ["my-tasks"] });
                }}
              />
            )}
            {SIMPLE_TURNOVER_V1 && activeWizardStepId === "keys" && (
              <SimpleKeysStep
                propertyId={propertyId!}
                taskId={task.id}
                entryWalkthroughState={task.entry_walkthrough_state}
                includeMaintenance={includeMaintenance}
                includeQuickCheck={includeQuickCheck}
                includeGuestSetup={includeGuestSetup}
                initialPhotoUrl={(task as { closeout_keys_photo_url?: string | null }).closeout_keys_photo_url}
                access={{
                  entry_location: property?.entry_location ?? null,
                  entry_method: property?.entry_method ?? null,
                  entry_code: property?.entry_code ?? null,
                  key_location_reference_urls:
                    (property as { key_location_reference_urls?: string[] | null } | null)
                      ?.key_location_reference_urls ?? [],
                }}
                onBack={goBackSimpleStep}
                onSelectSegment={(i) => void rewindSimpleToSegment(i)}
                onComplete={() => {
                  void queryClient.invalidateQueries({ queryKey: ["task-for-owner-team-runner", propertyId] });
                }}
              />
            )}
            {SIMPLE_TURNOVER_V1 && activeWizardStepId === "guest_setup" && task && (
              <GuestAmenitySetupStep
                propertyId={propertyId!}
                taskId={task.id}
                initialGuestSetupState={task.guest_setup_state ?? null}
                stagingLines={task.checklist_guest_amenity_staging ?? null}
                hostGuestAmenitiesInstructions={task.checklist_guest_amenities_instructions ?? null}
                guestCount={task.guest_count ?? null}
                simpleChrome
                includeMaintenance={includeMaintenance}
                includeQuickCheck={includeQuickCheck}
                includeGuestSetup={includeGuestSetup}
                onBack={goBackSimpleStep}
                onSelectSegment={(i) => void rewindSimpleToSegment(i)}
                onCompleted={() => {
                  void queryClient.invalidateQueries({ queryKey: ["task-for-owner-team-runner", propertyId] });
                }}
              />
            )}
            {SIMPLE_TURNOVER_V1 && activeWizardStepId === "inventory_single" && (
              <SimpleInventoryStep
                propertyId={propertyId!}
                cleaningTaskId={task.id}
                readOnly={bothCompleted}
                includeMaintenance={includeMaintenance}
                includeQuickCheck={includeQuickCheck}
                includeGuestSetup={includeGuestSetup}
                leftOutRecall={leftOutRecall}
                submitInventoryAsOwnerOrTeam={{
                  mutateAsync: submitInventoryAsOwnerOrTeam.mutateAsync,
                  isPending: submitInventoryAsOwnerOrTeam.isPending,
                }}
                onBack={goBackSimpleStep}
                onSelectSegment={(i) => void rewindSimpleToSegment(i)}
                onComplete={() => {
                  void queryClient.invalidateQueries({ queryKey: ["task-for-owner-team-runner", propertyId] });
                  void queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", propertyId] });
                  void queryClient.invalidateQueries({ queryKey: ["inventory-cleaner", propertyId] });
                }}
              />
            )}
            {SIMPLE_TURNOVER_V1 && activeWizardStepId === "maintenance" && (
              <SimpleMaintenanceStep
                propertyId={propertyId!}
                taskId={task.id}
                cleanerId={user?.id || ""}
                entryWalkthroughState={task.entry_walkthrough_state}
                checklistName={task.checklist_name}
                onComplete={async () => {
                  try {
                    await markTaskCompleteMutation.mutateAsync({
                      taskId: task.id,
                      propertyId: propertyId!,
                    });
                  } catch {
                    /* still advance */
                  }
                  setCleaningFlowState("complete");
                  void queryClient.invalidateQueries({ queryKey: ["task-for-owner-team-runner", propertyId] });
                }}
              />
            )}
            {((!SIMPLE_TURNOVER_V1 && activeWizardStepId === "cleaning") ||
              (!SIMPLE_TURNOVER_V1 && activeWizardStepId === "inventory_single")) && (
              <>
                {(cleaningFlowState === "inventory" || (cleaningFlowState === "checklist" && checklistDone)) && (
                  <Alert className="mb-4 border-green-200 bg-green-50">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-800 text-sm">
                      {cleaningFlowState === "inventory"
                        ? tDynamic("Cleaning checklist completed. Please finish inventory.")
                        : tDynamic("Cleaning checklist completed.")}
                    </AlertDescription>
                  </Alert>
                )}
                <div className="w-full space-y-4">
                  <div className={cn(activeWizardStepId !== "cleaning" && "hidden")}>
                    <CleaningTab
                      propertyId={propertyId!}
                      taskId={task.id}
                      readOnly={bothCompleted}
                      checklistId={task.checklist_id}
                      checklistName={task.checklist_name}
                      timerStartedAt={timerStartedAtForProperty}
                      onProgressChange={setProgressData}
                      onChecklistSubmitted={handleChecklistSubmitted}
                      submitCleaningAsOwnerOrTeam={{
                        mutateAsync: (params) =>
                          submitCleaningAsOwnerOrTeam.mutateAsync({ ...params, propertyId: propertyId! }),
                      }}
                      submitInventoryAsOwnerOrTeam={{
                        mutateAsync: submitInventoryAsOwnerOrTeam.mutateAsync,
                      }}
                      reimbursementMode={(property as { inventory_reimbursement_mode?: string })?.inventory_reimbursement_mode || "auto_pay"}
                      entryWalkthroughState={task.entry_walkthrough_state ?? null}
                      entryWalkthroughCompleted={Boolean(task.entry_walkthrough_completed)}
                      hideInlineRoomInventory={Boolean(
                        task.id && task.include_inventory_check !== false && postCleanHasInventory
                      )}
                      hideInlineElapsedLabel={minimalTurnoverChrome}
                    />
                  </div>
                  <div
                    className={cn(
                      activeWizardStepId !== "inventory_single" && "hidden"
                    )}
                  >
                    <InventoryCheckTab
                      propertyId={propertyId!}
                      reimbursementMode={(property as { inventory_reimbursement_mode?: string })?.inventory_reimbursement_mode || "auto_pay"}
                      readOnly={bothCompleted}
                      onInventorySubmitted={handleInventorySubmitted}
                      onCompleteCleaning={
                        task?.id
                          ? async () => {
                              await markTaskCompleteMutation.mutateAsync({
                                taskId: task.id,
                                propertyId: propertyId!,
                              });
                            }
                          : undefined
                      }
                      allTasksCompleted={
                        progressData.totalTasks > 0 && progressData.completedCount === progressData.totalTasks
                      }
                      cleaningTaskId={task.id}
                      scheduledDate={task.scheduled_date}
                      inventoryGuestPhaseCompleted={task.inventory_guest_phase_completed}
                      guestSetupCompleted={Boolean(task.guest_setup_completed)}
                      guestStagingLineCount={task.checklist_guest_amenity_staging?.length ?? 0}
                      wizardInventoryStepId={activeWizardStepId}
                      submitInventoryAsOwnerOrTeam={{
                        mutateAsync: submitInventoryAsOwnerOrTeam.mutateAsync,
                        isPending: submitInventoryAsOwnerOrTeam.isPending,
                      }}
                      propertySubtitle={inventoryPageSubtitle}
                    />
                  </div>
                </div>
              </>
            )}
            {activeWizardStepId === "done" && SIMPLE_TURNOVER_V1 && task.is_test && (
              <TestCleanFinished
                propertyId={task.property_id}
                taskId={task.id}
                startedAt={task.started_at}
                completedAt={task.completed_at}
                entryWalkthroughState={task.entry_walkthrough_state}
                endedEarly={Boolean((task.test_setup as { ended_early?: boolean } | null)?.ended_early)}
              />
            )}
            {activeWizardStepId === "done" && SIMPLE_TURNOVER_V1 && !task.is_test && (
              <SimpleDoneStep
                photoCount={(() => {
                  const s = readSimpleFlowState(task.entry_walkthrough_state);
                  return (
                    collectSimplePhotoUrls(s, "before").length +
                    collectSimplePhotoUrls(s, "after").length +
                    (s.keys_photo_url ? 1 : 0)
                  );
                })()}
                issueCount={
                  readSimpleFlowState(task.entry_walkthrough_state).inspect_findings?.filter(
                    (f) => f.status === "missing" || f.status === "broken",
                  ).length ?? 0
                }
                startedAt={task.started_at}
                taskId={task.id}
                finishedAt={task.completed_at}
                onJobs={() => navigate("/owner/properties")}
              />
            )}
            {activeWizardStepId === "done" && !SIMPLE_TURNOVER_V1 && (
              <Alert className="border-[color:color-mix(in_srgb,var(--pv-teal)_35%,var(--pv-line))] bg-[color:var(--pv-teal-soft)] text-[color:var(--pv-ink)] [&>svg]:text-[color:var(--pv-teal)]">
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription className="text-[color:var(--pv-ink-2)]">
                  {tDynamic("All steps for this turnover are complete.")}
                </AlertDescription>
              </Alert>
            )}
          </TurnoverWizard>
        ) : (
          <>
            {showEntryWalkthrough ? (
              <>
                <TurnoverStepStrip
                  phase={turnoverPhase}
                  showEntryStep={showEntryColumn}
                  splitInventory={false}
                  tDynamic={tDynamic}
                  className="mb-4"
                />
                <EntryWalkthroughStep
                  propertyId={propertyId!}
                  taskId={task.id}
                  checklistId={task.checklist_id}
                  cleanerId=""
                  onCompleted={() => {
                    void queryClient.invalidateQueries({ queryKey: ["task-for-owner-team-runner", propertyId] });
                  }}
                  tDynamic={tDynamic}
                />
              </>
            ) : (
              <>
        <CleaningTimer
          taskId={task.id}
          propertyId={propertyId}
          startedAt={task.status === "in_progress" ? (task.started_at ?? undefined) : undefined}
          completedAt={task.completed_at ?? undefined}
          durationMinutes={task.duration_minutes ?? undefined}
          scheduledDate={task.scheduled_date}
          unitEmptyOnArrival={task.unit_empty_on_arrival ?? undefined}
          propertyLat={property?.latitude ?? undefined}
          propertyLng={property?.longitude ?? undefined}
          propertyAddress={property?.formatted_address || [property?.address, property?.city].filter(Boolean).join(", ") || undefined}
        />

        {(cleaningFlowState === "inventory" || (cleaningFlowState === "checklist" && checklistDone)) && (
          <Alert className="mb-4 border-green-200 bg-green-50">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800 text-sm">
              {cleaningFlowState === "inventory"
                ? tDynamic("Cleaning checklist completed. Please finish inventory.")
                : tDynamic("Cleaning checklist completed.")}
            </AlertDescription>
          </Alert>
        )}

        <TurnoverStepStrip
          phase={turnoverPhase}
          showEntryStep={showEntryColumn}
          splitInventory={false}
          tDynamic={tDynamic}
          className="mb-4"
        />
        {(cleaningFlowState === "checklist" || cleaningFlowState === "inventory") && (
          <div className="w-full space-y-4">
            <div className={cn(activeTab !== "cleaning" && "hidden")}>
              <CleaningTab
                propertyId={propertyId!}
                taskId={task.id}
                readOnly={bothCompleted}
                checklistId={task.checklist_id}
                checklistName={task.checklist_name}
                timerStartedAt={task.status === "in_progress" ? task.started_at ?? undefined : undefined}
                onProgressChange={setProgressData}
                onChecklistSubmitted={handleChecklistSubmitted}
                submitCleaningAsOwnerOrTeam={{
                  mutateAsync: (params) =>
                    submitCleaningAsOwnerOrTeam.mutateAsync({ ...params, propertyId: propertyId! }),
                }}
                submitInventoryAsOwnerOrTeam={{
                  mutateAsync: submitInventoryAsOwnerOrTeam.mutateAsync,
                }}
                reimbursementMode={(property as { inventory_reimbursement_mode?: string })?.inventory_reimbursement_mode || "auto_pay"}
                entryWalkthroughState={task.entry_walkthrough_state ?? null}
                entryWalkthroughCompleted={Boolean(task.entry_walkthrough_completed)}
                hideInlineRoomInventory={Boolean(
                  task.id && task.include_inventory_check !== false && postCleanHasInventory
                )}
              />
            </div>
            <div className={cn(activeTab !== "inventory" && "hidden")}>
              {showGuestAmenitySetupLegacy && task ? (
                <GuestAmenitySetupStep
                  propertyId={propertyId!}
                  taskId={task.id}
                  initialGuestSetupState={task.guest_setup_state ?? null}
                  stagingLines={task.checklist_guest_amenity_staging ?? null}
                  hostGuestAmenitiesInstructions={task.checklist_guest_amenities_instructions ?? null}
                  onCompleted={() => {
                    invalidateTaskRunnerQuery(queryClient, propertyId, task.id);
                  }}
                />
              ) : (
                <InventoryCheckTab
                  propertyId={propertyId!}
                  reimbursementMode={(property as { inventory_reimbursement_mode?: string })?.inventory_reimbursement_mode || "auto_pay"}
                  readOnly={bothCompleted}
                  onInventorySubmitted={handleInventorySubmitted}
                  onCompleteCleaning={
                    task?.id
                      ? async () => {
                          await markTaskCompleteMutation.mutateAsync({
                            taskId: task.id,
                            propertyId: propertyId!,
                          });
                        }
                      : undefined
                  }
                  allTasksCompleted={
                    progressData.totalTasks > 0 && progressData.completedCount === progressData.totalTasks
                  }
                  cleaningTaskId={task.id}
                  scheduledDate={task.scheduled_date}
                  inventoryGuestPhaseCompleted={task.inventory_guest_phase_completed}
                  guestSetupCompleted={Boolean(task.guest_setup_completed)}
                  guestStagingLineCount={task.checklist_guest_amenity_staging?.length ?? 0}
                  submitInventoryAsOwnerOrTeam={{
                    mutateAsync: submitInventoryAsOwnerOrTeam.mutateAsync,
                    isPending: submitInventoryAsOwnerOrTeam.isPending,
                  }}
                  propertySubtitle={inventoryPageSubtitle}
                />
              )}
            </div>
          </div>
        )}
              </>
            )}
          </>
        )}
      </div>

      {(task as { cleaner_id?: string | null } | null)?.cleaner_id ? (
        <TurnoverUnitIssueReportDialog
          open={unitIssueReportOpen}
          onOpenChange={setUnitIssueReportOpen}
          propertyId={propertyId!}
          cleaningTaskId={task!.id}
          cleanerId={(task as { cleaner_id: string }).cleaner_id}
          checklistId={task!.checklist_id}
          checklistName={task!.checklist_name}
          tDynamic={tDynamic}
        />
      ) : null}
    </>
  );
};

export default TaskRunPage;
